/* Noxrivet product page: gallery + lightbox, variant selection, quantity, add-to-bag states,
   Complete the look recommendations and small dialogs. Adding to the bag itself is done by the
   theme's <product-form> (product-form.js); this file only guards and reports around it. */
(() => {
  if (window.nxpInit) return;
  window.nxpInit = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');
  const tpl = (s, d) => String(s || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (d[k] == null ? '' : d[k]));
  const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);

  /* ---------------------------------------------------------------- gallery */
  class NxpGallery extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.track = this.querySelector('[data-nxp-track]');
      if (!this.track) return;
      this.stage = this.querySelector('.nxp-gallery__stage');
      this.slides = [...this.querySelectorAll('[data-nxp-slide]')];
      this.thumbList = this.querySelector('[data-nxp-thumbs]');
      this.thumbs = [...this.querySelectorAll('[data-nxp-thumb]')];
      this.zoomBtn = this.querySelector('[data-nxp-zoom]');
      this.prevBtn = this.querySelector('[data-nxp-thumbs-prev]');
      this.nextBtn = this.querySelector('[data-nxp-thumbs-next]');
      this.index = 0;

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.go(Number(t.dataset.index))));
      let raf = 0;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => this.syncFromScroll());
        clearTimeout(this.settle);
        this.settle = setTimeout(() => this.fitStage(), 150);
      }, { passive: true });
      this.track.addEventListener('scrollend', () => this.fitStage(), { passive: true });
      this.track.addEventListener('click', (e) => {
        const img = e.target.closest('[data-nxp-zoomable]');
        if (img) this.openLightbox(img.closest('[data-nxp-slide]'), img);
      });
      this.zoomBtn?.addEventListener('click', () => this.openLightbox(null, this.zoomBtn));
      this.prevBtn?.addEventListener('click', () => this.pageThumbs(-1));
      this.nextBtn?.addEventListener('click', () => this.pageThumbs(1));
      this.thumbList?.addEventListener('scroll', () => this.updateThumbNav(), { passive: true });
      window.addEventListener('resize', () => {
        this.track.scrollLeft = this.index * this.track.clientWidth;
        this.updateThumbNav();
      });

      // Deferred slide images (see nxp-gallery.liquid): load after the page has loaded, or as soon
      // as the shopper interacts with the gallery.
      // Collected up front: the men/women toggle detaches the other set's slides, which must still load.
      this.deferredImgs = [...this.querySelectorAll('img[data-nx-src]')];
      this.reveal = () => {
        if (this.revealed) return;
        this.revealed = true;
        this.deferredImgs.forEach((img) => {
          if (img.dataset.nxSrcset) img.srcset = img.dataset.nxSrcset;
          img.src = img.dataset.nxSrc;
          img.removeAttribute('data-nx-src');
          img.removeAttribute('data-nx-srcset');
        });
      };
      ['pointerdown', 'touchstart', 'focusin', 'keydown', 'mouseover'].forEach((t) => this.addEventListener(t, this.reveal, { once: true, passive: true }));
      this.track.addEventListener('scroll', this.reveal, { once: true, passive: true });
      const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(this.reveal, { timeout: 2000 }) : setTimeout(this.reveal, 800));
      if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });

      const start = Number(this.dataset.start || 0);
      if (start > 0) this.reveal();
      this.setActive(start);
      if (start > 0) requestAnimationFrame(() => this.go(start, false));
      this.updateThumbNav();
      this.setupLightbox();
      this.setupGender();
    }

    /* ---- men / women photo toggle (only when media alt text ends in " – men" / " – women") ---- */
    setupGender() {
      this.genderBtn = this.querySelector('[data-nx-gender-toggle]');
      if (!this.genderBtn) return;
      const lis = this.thumbs.map((t) => t.parentElement);
      this.all = {
        slides: [...this.slides],
        thumbLis: lis,
        lbSlides: [...(this.lbSlides || [])]
      };
      let saved = null;
      try { saved = localStorage.getItem('nx_gender'); } catch (e) { /* storage blocked */ }
      const first = this.slides[0]?.dataset.nxGender;
      this.gender = null;
      const initial = saved === 'men' || saved === 'women' ? saved : first || 'women';
      if (first && initial !== first) this.reveal();
      this.applyGender(initial);
      this.genderBtn.addEventListener('click', () => {
        this.reveal();
        const next = this.gender === 'men' ? 'women' : 'men';
        try { localStorage.setItem('nx_gender', next); } catch (e) { /* storage blocked */ }
        this.applyGender(next);
      });
    }

    applyGender(g) {
      if (!this.all || g === this.gender) return;
      const keep = (el) => !el.dataset.nxGender || el.dataset.nxGender === g;
      const slides = this.all.slides.filter(keep);
      const lis = this.all.thumbLis.filter(keep);
      const lb = this.all.lbSlides.filter(keep);
      if (!slides.length) return;
      this.gender = g;
      this.dataset.nxGender = g;
      const total = slides.length;
      const renum = (el, n, count) => {
        let k = 0;
        const label = el.getAttribute('aria-label');
        if (label) el.setAttribute('aria-label', label.replace(/\d+/g, () => (k++ === 0 ? n + 1 : count)));
      };
      this.track.replaceChildren(...slides);
      slides.forEach((s, n) => { s.dataset.index = n; renum(s, n, total); });
      this.slides = slides;
      if (this.thumbList) {
        this.thumbList.replaceChildren(...lis);
        this.thumbs = lis.map((li) => li.querySelector('[data-nxp-thumb]'));
        this.thumbs.forEach((t, n) => { t.dataset.index = n; renum(t, n, total); });
      }
      if (this.lbTrack) {
        this.lbTrack.replaceChildren(...lb);
        this.lbSlides = lb;
        const c = this.lb.querySelector('.nxp-lb__count');
        if (c && c.lastChild) c.lastChild.textContent = ` / ${lb.length}`;
        this.unzoom();
      }
      this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.setActive(0);
      this.fitStage(0);
      if (this.thumbList) this.thumbList.scrollLeft = 0;
      this.updateThumbNav();
      const other = g === 'men' ? 'women' : 'men';
      this.genderBtn.setAttribute('aria-label', `Unisex. Showing ${g} photos. Switch to ${other} photos`);
    }

    syncFromScroll() {
      const w = this.track.clientWidth;
      if (!w) return;
      const i = clamp(Math.round(this.track.scrollLeft / w), 0, this.slides.length - 1);
      if (i !== this.index) this.setActive(i);
    }

    // Native-ratio photo (nx-product-native-ratio.css): the stage takes the shape of the slide the
    // gallery lands on; any other slide returns it to the section's frame ratio. Never changed while a
    // scroll is under way: resizing the scroller mid-scroll makes the browser re-snap and stop early.
    fitStage(i = this.index) {
      if (!this.stage) return;
      const slide = this.slides[i];
      const r = slide?.dataset.nxNativeFrame === 'true' ? slide.style.getPropertyValue('--nx-native-ratio').trim() : '';
      if (r) this.stage.style.setProperty('--nx-stage-ratio', r);
      else this.stage.style.removeProperty('--nx-stage-ratio');
    }

    go(i, smooth = true) {
      this.reveal?.();
      i = clamp(i, 0, this.slides.length - 1);
      this.fitStage(i);
      this.track.scrollTo({ left: i * this.track.clientWidth, behavior: smooth ? behavior() : 'auto' });
      this.setActive(i);
    }

    showMedia(mediaId) {
      let i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i < 0 && this.all) {
        const hit = this.all.slides.find((s) => s.dataset.mediaId === String(mediaId));
        if (hit && hit.dataset.nxGender) {
          this.applyGender(hit.dataset.nxGender);
          i = this.slides.indexOf(hit);
        }
      }
      if (i >= 0 && i !== this.index) this.go(i);
    }

    setActive(i) {
      this.index = i;
      this.thumbs.forEach((t, n) => {
        if (n === i) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      const slide = this.slides[i];
      if (this.zoomBtn) this.zoomBtn.hidden = !slide || slide.dataset.type !== 'image';
      this.slides.forEach((s, n) => {
        if (n !== i) s.querySelectorAll('video').forEach((v) => v.pause());
      });
      const thumb = this.thumbs[i];
      if (thumb && this.thumbList) {
        const li = thumb.parentElement;
        const left = li.offsetLeft;
        const right = left + li.offsetWidth;
        const view = this.thumbList.scrollLeft;
        if (left < view) this.thumbList.scrollTo({ left, behavior: behavior() });
        else if (right > view + this.thumbList.clientWidth) this.thumbList.scrollTo({ left: right - this.thumbList.clientWidth, behavior: behavior() });
      }
    }

    pageThumbs(dir) {
      if (!this.thumbList) return;
      this.thumbList.scrollBy({ left: dir * this.thumbList.clientWidth * 0.75, behavior: behavior() });
    }

    updateThumbNav() {
      if (!this.thumbList || !this.prevBtn || !this.nextBtn) return;
      const l = this.thumbList;
      this.prevBtn.disabled = l.scrollLeft <= 2;
      this.nextBtn.disabled = l.scrollLeft + l.clientWidth >= l.scrollWidth - 2;
    }

    /* ---- lightbox ---- */
    setupLightbox() {
      this.lb = this.querySelector('[data-nxp-lb]');
      if (!this.lb) return;
      this.lbTrack = this.lb.querySelector('[data-nxp-lb-track]');
      this.lbSlides = [...this.lb.querySelectorAll('[data-nxp-lb-slide]')];
      this.lbCount = this.lb.querySelector('[data-nxp-lb-count]');
      this.lbPrev = this.lb.querySelector('[data-nxp-lb-prev]');
      this.lbNext = this.lb.querySelector('[data-nxp-lb-next]');
      this.lbHint = this.lb.querySelector('.nxp-lb__hint');
      this.lbIndex = 0;

      this.lb.querySelector('[data-nxp-lb-close]')?.addEventListener('click', () => this.lb.close());
      this.lbPrev?.addEventListener('click', () => this.lbGo(this.lbIndex - 1));
      this.lbNext?.addEventListener('click', () => this.lbGo(this.lbIndex + 1));
      this.lb.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.lbGo(this.lbIndex - 1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); this.lbGo(this.lbIndex + 1); }
      });
      let raf = 0;
      this.lbTrack.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const w = this.lbTrack.clientWidth;
          if (!w) return;
          const i = clamp(Math.round(this.lbTrack.scrollLeft / w), 0, this.lbSlides.length - 1);
          if (i !== this.lbIndex) { this.unzoom(); this.lbIndex = i; this.updateLb(); }
        });
      }, { passive: true });
      this.lbTrack.addEventListener('click', (e) => {
        const img = e.target.closest('.nxp-lb__img');
        if (img) this.toggleZoom(img.closest('[data-nxp-lb-slide]'), img, e);
      });
      this.lbTrack.addEventListener('mousemove', (e) => {
        const slide = this.lbSlides[this.lbIndex];
        if (!slide || !slide.classList.contains('is-zoomed')) return;
        const img = slide.querySelector('img');
        slide.scrollLeft = (e.clientX / slide.clientWidth) * (img.offsetWidth - slide.clientWidth);
        slide.scrollTop = (e.clientY / slide.clientHeight) * (img.offsetHeight - slide.clientHeight);
      });
      this.lb.addEventListener('close', () => {
        this.unzoom();
        document.documentElement.classList.remove('nxp-lock');
        const mid = this.lbSlides[this.lbIndex]?.dataset.mediaId;
        if (mid) {
          const i = this.slides.findIndex((s) => s.dataset.mediaId === mid);
          if (i >= 0) this.go(i, false);
        }
        const back = this.opener && this.opener.isConnected ? this.opener : this.zoomBtn;
        back?.focus({ preventScroll: true });
      });
      window.addEventListener('resize', () => {
        if (this.lb.open) this.lbTrack.scrollLeft = this.lbIndex * this.lbTrack.clientWidth;
      });
    }

    loadLb(i) {
      const img = this.lbSlides[i]?.querySelector('img');
      if (img && img.dataset.src && !img.getAttribute('src')) {
        img.srcset = img.dataset.srcset || '';
        img.src = img.dataset.src;
      }
    }

    openLightbox(fromSlide, opener) {
      if (!this.lb) return;
      const slide = fromSlide || this.slides[this.index];
      let i = this.lbSlides.findIndex((s) => s.dataset.mediaId === slide?.dataset.mediaId);
      if (i < 0) i = 0;
      this.opener = opener || document.activeElement;
      this.lbIndex = i;
      [i, i - 1, i + 1].forEach((n) => this.loadLb(n));
      this.lb.showModal();
      document.documentElement.classList.add('nxp-lock');
      this.lbTrack.scrollLeft = i * this.lbTrack.clientWidth;
      this.updateLb();
      this.lb.querySelector('[data-nxp-lb-close]')?.focus();
    }

    lbGo(i) {
      i = clamp(i, 0, this.lbSlides.length - 1);
      if (i === this.lbIndex) return;
      this.unzoom();
      this.lbIndex = i;
      this.lbTrack.scrollTo({ left: i * this.lbTrack.clientWidth, behavior: behavior() });
      this.updateLb();
    }

    updateLb() {
      const i = this.lbIndex;
      if (this.lbCount) this.lbCount.textContent = String(i + 1);
      if (this.lbPrev) this.lbPrev.disabled = i === 0;
      if (this.lbNext) this.lbNext.disabled = i === this.lbSlides.length - 1;
      [i, i - 1, i + 1].forEach((n) => this.loadLb(n));
    }

    toggleZoom(slide, img, e) {
      if (!slide) return;
      const zoom = !slide.classList.contains('is-zoomed');
      this.unzoom();
      if (!zoom) return;
      const r = img.getBoundingClientRect();
      const rx = clamp((e.clientX - r.left) / r.width, 0, 1);
      const ry = clamp((e.clientY - r.top) / r.height, 0, 1);
      // zoom shows the largest file and at least 2.5x the fitted size
      if (img.dataset.full && img.dataset.zoomed !== '1') {
        img.dataset.zoomed = '1';
        img.removeAttribute('srcset');
        img.removeAttribute('sizes');
        img.src = img.dataset.full;
      }
      const fullW = Number(img.getAttribute('width')) || 2400;
      slide.style.setProperty('--nxp-zoom-w', `${Math.max(fullW, r.width * 2.5)}px`);
      slide.classList.add('is-zoomed');
      this.lbTrack.style.overflowX = 'hidden';
      if (this.lbHint) this.lbHint.hidden = true;
      requestAnimationFrame(() => {
        slide.scrollLeft = rx * img.offsetWidth - slide.clientWidth / 2;
        slide.scrollTop = ry * img.offsetHeight - slide.clientHeight / 2;
      });
    }

    unzoom() {
      this.lbSlides?.forEach((s) => {
        s.classList.remove('is-zoomed');
        s.scrollTop = 0;
        s.scrollLeft = 0;
      });
      if (this.lbTrack) this.lbTrack.style.overflowX = '';
    }
  }
  customElements.define('nxp-gallery', NxpGallery);

  /* ---------------------------------------------------------------- product */
  class NxpProduct extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const json = this.querySelector('[data-nxp-data]');
      if (!json) return;
      this.data = JSON.parse(json.textContent);
      this.s = this.data.strings;
      this.variants = this.data.variants;
      this.picker = this.querySelector('[data-nxp-picker]');
      this.pf = this.querySelector('product-form');
      this.idInput = this.querySelector('[data-nxp-id]');
      this.atc = this.querySelector('[data-nxp-atc]');
      this.atcLabel = this.querySelector('[data-nxp-atc-label]');
      this.status = this.querySelector('[data-nxp-status]');
      this.errorWrap = this.querySelector('.product-form__error-message-wrapper');
      this.gallery = this.querySelector('nxp-gallery');
      this.qtyInput = this.querySelector('.nxp-qty__input');
      this.dynamic = this.querySelector('[data-nxp-dynamic]');
      this.dynamicInner = this.querySelector('[data-nxp-dynamic-inner]');
      // Accelerated checkout is off until a size is chosen; a click on it then asks for the size like Add to bag.
      this.dynamic?.addEventListener('click', () => {
        if (this.dynamic.dataset.state === 'pending') this.promptMissing();
      });

      if (this.picker) {
        this.selection = this.data.options.map((_, i) => {
          const checked = this.picker.querySelector(`.nxp-opt__input[data-index="${i}"]:checked`);
          return checked ? checked.value : null;
        });
        this.picker.addEventListener('change', (e) => {
          const input = e.target.closest('.nxp-opt__input');
          if (input) this.onSelect(input);
        });
      } else {
        this.selection = null;
      }
      this.update(false);

      this.pf?.addEventListener('submit', (e) => this.onSubmit(e), true);
      this.setupQty();
      this.watchCart();
      this.setupSticky();
    }

    // Mobile sticky Add to bag. Mirrors the main button's label/disabled state and the shown
    // price; a tap presses the main button, which already asks for a missing size.
    setupSticky() {
      const bar = this.querySelector('[data-nxp-sticky]');
      if (!bar || !this.atc || !('IntersectionObserver' in window)) return;
      const btn = bar.querySelector('[data-nxp-sticky-btn]');
      const label = bar.querySelector('[data-nxp-sticky-label]');
      const price = bar.querySelector('[data-nxp-sticky-price]');
      const priceNow = this.querySelector('[data-nxp-price-now]');
      const mq = window.matchMedia('(max-width: 749px)');
      let atcVisible = true;
      let scrollRaf = 0;
      let footerVisible = false;
      const set = () => {
        // Show only once the visitor has scrolled a little, so the bar never covers the product title on load.
        const show = mq.matches && !atcVisible && !footerVisible && window.scrollY > 160;
        bar.dataset.visible = show ? 'true' : 'false';
        bar.inert = !show;
        document.documentElement.classList.toggle('nxp-has-sticky', show);
      };
      const sync = () => {
        if (priceNow && priceNow.textContent.trim()) price.textContent = priceNow.textContent.trim();
        if (this.atcLabel) label.textContent = this.atcLabel.textContent.trim();
        btn.disabled = this.atc.disabled;
      };
      new IntersectionObserver((es) => { atcVisible = es[0].isIntersecting; set(); }).observe(this.atc);
      window.addEventListener('scroll', () => { if (!scrollRaf) scrollRaf = requestAnimationFrame(() => { scrollRaf = 0; set(); }); }, { passive: true });
      const footer = document.querySelector('footer');
      if (footer) new IntersectionObserver((es) => { footerVisible = es[0].isIntersecting; set(); }).observe(footer);
      mq.addEventListener?.('change', set);
      this.addEventListener('nxp:variant-change', sync);
      sync();
      btn.addEventListener('click', () => {
        if (this.atc.disabled) { this.atc.scrollIntoView({ block: 'center', behavior: behavior() }); return; }
        this.atc.click();
      });
    }

    find(sel) {
      return this.variants.find((v) => v.options.every((o, i) => o === sel[i])) || null;
    }

    onSelect(input) {
      const i = Number(input.dataset.index);
      this.selection[i] = input.value;
      const fs = input.closest('[data-nxp-option]');
      const label = fs?.querySelector('[data-nxp-selected]');
      if (label) label.textContent = input.value;
      if (fs) this.clearInvalid(fs);
      this.update(true);
    }

    update(fromUser) {
      let v = null;
      let complete = true;
      if (this.selection) {
        complete = this.selection.every((x) => x != null);
        v = complete ? this.find(this.selection) : null;
        this.refreshStates();
      } else {
        v = this.variants.find((x) => String(x.id) === this.idInput?.value) || this.variants[0] || null;
      }
      this.variant = v;
      if (this.idInput) this.idInput.value = v ? v.id : '';

      if (v || complete) this.setPrice(v);
      if (this.atc && this.atcLabel) {
        let label = this.s.add;
        let disabled = false;
        if (complete && !v) { label = this.s.unavailable; disabled = true; }
        else if (v && !v.available) { label = this.s.soldOut; disabled = true; }
        this.atcLabel.textContent = label;
        this.atc.disabled = disabled;
      }
      this.applyQtyRules(v);
      this.syncDynamic(complete, v);

      if (fromUser) {
        if (this.errorWrap) this.errorWrap.hidden = true;
        this.setStatus('');
        const media = v ? v.media : this.partialMedia();
        if (media) this.gallery?.showMedia(media);
        if (v && this.dataset.updateUrl === 'true') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', v.id);
          window.history.replaceState(window.history.state, '', url.toString());
        }
      }
      this.dispatchEvent(new CustomEvent('nxp:variant-change', { bubbles: true, detail: { variant: v } }));
    }

    // Before every option is chosen (e.g. color picked, size not yet), show the photo the
    // matching variants share, so the gallery follows the chosen color.
    partialMedia() {
      if (!this.selection) return null;
      const ids = new Set();
      this.variants.forEach((x) => {
        if (x.media && x.options.every((o, i) => this.selection[i] == null || o === this.selection[i])) ids.add(x.media);
      });
      return ids.size === 1 ? [...ids][0] : null;
    }

    syncDynamic(complete, v) {
      if (!this.dynamic) return;
      const state = v && v.available ? 'on' : !complete ? 'pending' : 'off';
      this.dynamic.dataset.state = state;
      if (this.dynamicInner) this.dynamicInner.inert = state !== 'on';
    }

    refreshStates() {
      this.picker.querySelectorAll('.nxp-opt__input').forEach((input) => {
        const i = Number(input.dataset.index);
        const matches = this.variants.filter((v) =>
          v.options[i] === input.value && v.options.every((o, j) => j === i || this.selection[j] == null || o === this.selection[j])
        );
        const state = !matches.length ? 'unavailable' : matches.some((m) => m.available) ? 'available' : 'soldout';
        input.dataset.state = state;
        const sr = input.nextElementSibling?.querySelector('[data-nxp-state]');
        if (sr) sr.textContent = state === 'soldout' ? this.s.stateSoldOut : state === 'unavailable' ? this.s.stateUnavailable : '';
      });
    }

    // Selling price, comparison and savings always come from the same variant (amounts are
    // Liquid money output in the active currency). v = null (unavailable combination) keeps the
    // shown price but drops the comparison, so no savings claim outlives its variant.
    setPrice(v) {
      const now = this.querySelector('[data-nxp-price-now]');
      const nowLabel = this.querySelector('[data-nx-pp-now-label]');
      const wasWrap = this.querySelector('[data-nxp-price-was-wrap]');
      const was = this.querySelector('[data-nxp-price-was]');
      const save = this.querySelector('[data-nx-pp-save]');
      const compare = v && v.compare && v.save ? v.compare : null;
      if (now && v) {
        const cur = this.s.currency;
        now.innerHTML = '<span class="nx-pp__amt">' + v.price + '</span>' + (cur ? ' <span class="nx-pp__cur">' + cur + '</span>' : '');
      }
      if (nowLabel) nowLabel.textContent = compare ? this.s.salePrice : this.s.regularPrice;
      if (wasWrap && was) {
        wasWrap.hidden = !compare;
        was.innerHTML = compare || '';
      }
      if (save) {
        save.hidden = !compare;
        save.innerHTML = compare ? v.save : '';
      }
    }

    /* ---- quantity ---- */
    rules() {
      const v = this.variant || this.variants[0] || {};
      return { min: v.qmin || 1, max: v.qmax || Infinity, step: v.qstep || 1 };
    }

    normalise(n) {
      const { min, max, step } = this.rules();
      n = Number.isFinite(n) ? Math.round(n) : min;
      n = Math.round(n / step) * step;
      return clamp(n, min, max === Infinity ? Number.MAX_SAFE_INTEGER : max - (max % step));
    }

    setupQty() {
      if (!this.qtyInput) return;
      this.querySelectorAll('[data-nxp-step]').forEach((b) => b.addEventListener('click', () => {
        const { step } = this.rules();
        const cur = parseInt(this.qtyInput.value, 10) || 0;
        this.qtyInput.value = this.normalise(cur + Number(b.dataset.nxpStep) * step);
        this.syncQtyButtons();
      }));
      this.qtyInput.addEventListener('change', () => {
        this.qtyInput.value = this.normalise(parseInt(this.qtyInput.value, 10));
        this.syncQtyButtons();
      });
      this.syncQtyButtons();
    }

    applyQtyRules(v) {
      if (!this.qtyInput || !v) return;
      this.qtyInput.min = v.qmin;
      this.qtyInput.step = v.qstep;
      if (v.qmax) this.qtyInput.max = v.qmax;
      else this.qtyInput.removeAttribute('max');
      this.qtyInput.value = this.normalise(parseInt(this.qtyInput.value, 10));
      this.syncQtyButtons();
    }

    syncQtyButtons() {
      if (!this.qtyInput) return;
      const { min, max } = this.rules();
      const n = parseInt(this.qtyInput.value, 10) || min;
      const [minus, plus] = this.querySelectorAll('[data-nxp-step]');
      if (minus) minus.setAttribute('aria-disabled', String(n <= min));
      if (plus) plus.disabled = n >= max;
    }

    /* ---- submit guard + feedback ---- */
    onSubmit(e) {
      if (!this.variant) {
        e.preventDefault();
        e.stopPropagation();
        this.promptMissing();
        return;
      }
      if (!this.variant.available) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (this.qtyInput) this.qtyInput.value = this.normalise(parseInt(this.qtyInput.value, 10));
      this.setStatus('');
    }

    promptMissing() {
      const groups = [...this.querySelectorAll('[data-nxp-option]')];
      const missing = groups.find((fs) => !fs.querySelector('.nxp-opt__input:checked')) || groups[0];
      if (!missing) return;
      const name = missing.querySelector('.nxp-opt__name')?.textContent.trim().toLowerCase() || '';
      const err = missing.querySelector('[data-nxp-error]');
      missing.classList.add('is-invalid');
      if (err) {
        err.setAttribute('role', 'alert');
        err.textContent = tpl(this.s.select, { option: name });
        err.hidden = false;
      }
      const first = missing.querySelector('.nxp-opt__input:not([data-state="unavailable"])') || missing.querySelector('.nxp-opt__input');
      first?.focus({ preventScroll: true });
      missing.scrollIntoView({ block: 'center', behavior: behavior() });
    }

    clearInvalid(fs) {
      fs.classList.remove('is-invalid');
      const err = fs.querySelector('[data-nxp-error]');
      if (err) { err.hidden = true; err.textContent = ''; err.removeAttribute('role'); }
    }

    setStatus(text) {
      if (this.status) this.status.textContent = text;
    }

    watchCart() {
      // constants.js / pubsub.js declare these as script-level globals (not window properties).
      /* global subscribe, PUB_SUB_EVENTS */
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, (ev) => {
          if (ev && ev.source === 'product-form' && String(ev.productVariantId) === String(this.idInput?.value)) {
            this.gotResult = true;
          }
        });
        subscribe(PUB_SUB_EVENTS.cartError, (ev) => {
          if (ev && ev.source === 'product-form') this.gotResult = true;
        });
      }
      if (!this.atc) return;
      // product-form.js toggles .loading while the request runs; if it ends without a cart
      // response (network failure), say so instead of failing silently.
      let wasLoading = false;
      new MutationObserver(() => {
        const loading = this.atc.classList.contains('loading');
        if (loading && !wasLoading) this.gotResult = false;
        if (!loading && wasLoading && !this.gotResult && this.errorWrap) {
          const msg = this.errorWrap.querySelector('.product-form__error-message');
          if (msg && this.errorWrap.hidden) {
            msg.textContent = this.s.error;
            this.errorWrap.hidden = false;
          }
        }
        wasLoading = loading;
      }).observe(this.atc, { attributes: true, attributeFilter: ['class'] });
    }
  }
  customElements.define('nxp-product', NxpProduct);

  /* ---------------------------------------------------------------- recommendations */
  class NxpRecs extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      const target = this.parentElement || this;
      // Load once the page is idle (not when scrolled near), so the row is filled before a
      // visitor reaches it and later sections don't jump (CLS). Scrolling near still loads it.
      const start = () => this.load();
      const idle = () => ('requestIdleCallback' in window ? requestIdleCallback(start, { timeout: 2500 }) : setTimeout(start, 1200));
      if (document.readyState === 'complete') idle(); else window.addEventListener('load', idle, { once: true });
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) { io.disconnect(); this.load(); }
      }, { rootMargin: '0px 0px 600px 0px' });
      io.observe(target);
    }

    async load() {
      if (this.loading) return;
      this.loading = true;
      const limit = Number(this.dataset.limit || 3);
      const type = (this.dataset.type || '').toLowerCase();
      for (const intent of ['complementary', 'related']) {
        try {
          const res = await fetch(`${this.dataset.url}&intent=${intent}`);
          if (!res.ok) continue;
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
          let cards = [...doc.querySelectorAll('[data-nxp-ctl-grid] .nxp-card')];
          if (!cards.length) continue;
          if (intent === 'related' && type) {
            const other = cards.filter((c) => (c.dataset.type || '').toLowerCase() !== type);
            cards = [...other, ...cards.filter((c) => !other.includes(c))];
          }
          const grid = this.querySelector('[data-nxp-ctl-grid]');
          grid.replaceChildren(...cards.slice(0, limit).map((c) => document.importNode(c, true)));
          this.dataset.intent = intent;
          // "Complete the look" only for complementary outfit pieces; related products are alternatives.
          const outfit = intent === 'complementary';
          const title = this.querySelector('.nxp-ctl__title');
          const heading = outfit ? this.dataset.heading : this.dataset.altHeading;
          if (title && heading) title.textContent = heading;
          const sub = this.querySelector('.nxp-ctl__sub');
          if (sub) sub.hidden = !outfit;
          this.hidden = false;
          return;
        } catch (e) {
          /* try the next intent */
        }
      }
    }
  }
  customElements.define('nxp-recs', NxpRecs);

  /* letterbox matte: assets/nx-matte.js */

  /* ---------------------------------------------------------------- simple dialogs */
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-nxp-dialog-open]');
    if (open) {
      const d = document.getElementById(open.dataset.nxpDialogOpen);
      if (d && typeof d.showModal === 'function') {
        d.nxpOpener = open;
        d.showModal();
        d.querySelector('[data-nxp-dialog-close]')?.focus();
      }
      return;
    }
    const close = e.target.closest('[data-nxp-dialog-close]');
    if (close) close.closest('dialog')?.close();
  });
  // Delegated so dialogs re-rendered by the theme editor keep working.
  document.addEventListener('click', (e) => {
    if (e.target instanceof HTMLDialogElement && e.target.matches('[data-nxp-dialog]')) e.target.close();
  });
  document.addEventListener('close', (e) => {
    if (e.target instanceof HTMLDialogElement && e.target.matches('[data-nxp-dialog]')) e.target.nxpOpener?.focus();
  }, true);
})();
